import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import {
  ResourceInput,
  ResourceKind,
  TIME_ZONE,
  checkBooking,
  localMidnight,
  type BlackoutInput,
  type Booking,
  type BookingInput,
  type Resource,
  type RuleViolation,
  type TakenSlot,
} from '@syndiqia/reservations/contracts';
import { ApiError, errorMessage, useApi } from '../api';
import { useMyUnits } from '../comms-queries';
import { Alert, Disclosure, Empty, Field, Loading, PageHeader, toNumber, validate, type FieldErrors } from '../components/ui';
import { useI18n } from '../i18n';
import { useUnits } from '../queries';
import { useSyndicate } from '../session';

type Tab = 'book' | 'mine' | 'all' | 'facilities';

function useReservations<T>(key: unknown[], path: string, enabled = true) {
  const api = useApi();
  const s = useSyndicate();
  return useQuery({ queryKey: [s.tenantId, 'reservations', ...key], queryFn: () => api<T>(`${s.api}/reservations${path}`), enabled });
}
function useReservationMutation<Input, Output>(request: (api: ReturnType<typeof useApi>, base: string, input: Input) => Promise<Output>) {
  const api = useApi();
  const s = useSyndicate();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Input) => request(api, `${s.api}/reservations`, input),
    onSuccess: () => Promise.all(['reservations', 'ledger'].map((key) => queryClient.invalidateQueries({ queryKey: [s.tenantId, key] }))),
  });
}

const timeOf = (iso: string, locale: 'fr' | 'en') => new Intl.DateTimeFormat(locale === 'fr' ? 'fr-CA' : 'en-CA', { timeStyle: 'short', timeZone: TIME_ZONE }).format(new Date(iso));
const dayOf = (iso: string, locale: 'fr' | 'en') => new Intl.DateTimeFormat(locale === 'fr' ? 'fr-CA' : 'en-CA', { weekday: 'long', day: 'numeric', month: 'long', timeZone: TIME_ZONE }).format(new Date(iso));
const hhmm = (minutes: number) => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
const toMinutes = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
const addDays = (date: string, n: number) => {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const todayLocal = () => new Intl.DateTimeFormat('en-CA', { timeZone: TIME_ZONE }).format(new Date());

/** A user-facing reason for a refused booking. */
function bookingError(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  if (error instanceof ApiError) {
    if (error.status === 409) return t.reservations.rules.overlap;
    const rule = /^Booking rule: (\w+)$/.exec(error.body?.message ?? '')?.[1] as RuleViolation | undefined;
    if (rule && rule in t.reservations.rules) return t.reservations.rules[rule];
  }
  return errorMessage(error, t);
}

export function ReservationsPage() {
  const { t } = useI18n();
  const s = useSyndicate();
  const r = t.reservations;
  const manages = s.can('reservations.manage');
  const [tab, setTab] = useState<Tab>('book');
  if (!s.can('reservations.book') && !manages) return <Alert tone="info">{t.common.forbidden}</Alert>;
  const tabs: Tab[] = manages ? ['book', 'mine', 'all', 'facilities'] : ['book', 'mine'];
  return (
    <>
      <PageHeader title={r.title} intro={r.intro} />
      <nav className="subnav steps">
        {tabs.map((key) => (
          <button key={key} type="button" className={tab === key ? 'active' : undefined} onClick={() => setTab(key)}>
            {r.tabs[key]}
          </button>
        ))}
      </nav>
      {tab === 'book' && <BookTab />}
      {tab === 'mine' && <BookingsList scope="mine" />}
      {tab === 'all' && <BookingsList scope="all" />}
      {tab === 'facilities' && <FacilitiesTab />}
    </>
  );
}

// --- Book

function BookTab() {
  const { t } = useI18n();
  const r = t.reservations;
  const resources = useReservations<Resource[]>(['resources'], '/resources');
  const [selected, setSelected] = useState<string | null>(null);
  const active = (resources.data ?? []).filter((x) => x.active);
  const resource = active.find((x) => x.id === selected) ?? active[0];
  if (resources.isPending) return <Loading label={t.loading} />;
  if (resources.isError) return <Alert>{errorMessage(resources.error, t)}</Alert>;
  if (!resource) return <Empty>{r.noFacilities}</Empty>;
  return (
    <>
      <div className="facility-picker">
        {active.map((x) => (
          <button key={x.id} type="button" className={`facility${x.id === resource.id ? ' active' : ''}`} onClick={() => setSelected(x.id)}>
            <span className="badge">{r.kinds[x.kind]}</span>
            <strong>{x.name}</strong>
          </button>
        ))}
      </div>
      <FacilityRules resource={resource} />
      <BookingForm key={resource.id} resource={resource} />
    </>
  );
}

function FacilityRules({ resource: x }: { resource: Resource }) {
  const { t, money } = useI18n();
  const r = t.reservations;
  const facts = [
    r.hours(x.opensAt, x.closesAt),
    r.duration(x.minMinutes, x.maxMinutes),
    x.feeCents ? r.fee(money(x.feeCents)) : r.free,
    x.depositCents ? r.deposit(money(x.depositCents)) : null,
    x.weeklyQuota ? r.quota(x.weeklyQuota) : null,
    x.requiresApproval ? r.approval : null,
    r.cancelRule(x.cancelNoticeHours),
    r.ahead(x.maxDaysAhead),
  ].filter(Boolean);
  return (
    <div className="panel facility-rules">
      {x.description && <p>{x.description}</p>}
      <p className="small muted">{facts.join(' · ')}</p>
      {x.rulesText && <p className="small message-body">{x.rulesText}</p>}
    </div>
  );
}

function BookingForm({ resource }: { resource: Resource }) {
  const { t, locale, money } = useI18n();
  const r = t.reservations;
  const s = useSyndicate();
  const manages = s.can('reservations.manage');
  const units = useUnits();
  const myUnits = useMyUnits();
  const [day, setDay] = useState(addDays(todayLocal(), 1));
  const [start, setStart] = useState<number | null>(null);
  const [length, setLength] = useState(resource.minMinutes);
  const [unitId, setUnitId] = useState('');
  const [notes, setNotes] = useState('');
  const [done, setDone] = useState<Booking | null>(null);
  const from = localMidnight(day);
  const to = localMidnight(addDays(day, 1));
  const taken = useReservations<TakenSlot[]>(['availability', resource.id, day], `/resources/${resource.id}/availability?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
  const book = useReservationMutation<BookingInput, Booking>((api, base, input) => api(`${base}/bookings`, { method: 'POST', body: input }));

  const choices = manages ? (units.data ?? []) : (units.data ?? []).filter((u) => myUnits.data?.includes(u.id));
  const unit = unitId || choices[0]?.id || '';
  const at = (minutes: number) => new Date(Date.parse(from) + minutes * 60_000);
  const slots: number[] = [];
  for (let m = toMinutes(resource.opensAt); m < toMinutes(resource.closesAt); m += resource.slotMinutes) slots.push(m);
  const busy = (m: number) => (taken.data ?? []).find((x) => Date.parse(x.start) < at(m + resource.slotMinutes).getTime() && Date.parse(x.end) > at(m).getTime());
  const lengths: number[] = [];
  for (let l = resource.minMinutes; l <= resource.maxMinutes; l += resource.slotMinutes) lengths.push(l);
  const overlap = start !== null && slots.filter((m) => m >= start && m < start + length).some((m) => busy(m));
  const violation = start === null ? null : checkBooking(resource, at(start), at(start + length), new Date(), 0);
  const blocked = start === null || overlap || (violation !== null && !(manages && (violation === 'notice' || violation === 'too_far')));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (start === null || !unit) return;
    book.mutate(
      { resourceId: resource.id, unitId: unit, start: at(start).toISOString(), end: at(start + length).toISOString(), notes },
      { onSuccess: (b) => (setDone(b), setStart(null), setNotes('')) },
    );
  };

  if (!manages && myUnits.data && choices.length === 0) return <Alert tone="info">{r.noUnit}</Alert>;
  return (
    <form className="panel form booking-form" onSubmit={submit}>
      <div className="day-nav">
        <button type="button" className="button button-small" onClick={() => (setDay(addDays(day, -1)), setStart(null))} disabled={day <= todayLocal()}>
          ←
        </button>
        <input type="date" value={day} min={todayLocal()} onChange={(e) => (setDay(e.target.value), setStart(null))} />
        <strong>{dayOf(at(12 * 60).toISOString(), locale)}</strong>
        <button type="button" className="button button-small" onClick={() => (setDay(addDays(day, 1)), setStart(null))}>
          →
        </button>
      </div>
      <div className="slots" role="listbox" aria-label={r.start}>
        {slots.map((m) => {
          const b = busy(m);
          const inChoice = start !== null && m >= start && m < start + length;
          const cls = b ? (b.status === 'blackout' ? 'slot-closed' : b.mine ? 'slot-mine' : 'slot-taken') : inChoice ? 'slot-chosen' : 'slot-free';
          return (
            <button key={m} type="button" className={`slot ${cls}`} disabled={!!b} onClick={() => (setStart(m), setDone(null))} title={b ? (b.status === 'blackout' ? (b.reason ?? r.closed) : b.mine ? r.yours : r.taken) : r.free_}>
              {hhmm(m)}
            </button>
          );
        })}
      </div>
      <p className="small muted slot-legend">
        <span className="slot slot-free" /> {r.free_} <span className="slot slot-taken" /> {r.taken} <span className="slot slot-mine" /> {r.yours} <span className="slot slot-closed" /> {r.closed}
      </p>
      <div className="row">
        <Field label={r.length}>
          <select value={length} onChange={(e) => setLength(Number(e.target.value))}>
            {lengths.map((l) => (
              <option key={l} value={l}>
                {l < 60 ? `${l} min` : `${Math.floor(l / 60)} h${l % 60 ? ` ${l % 60}` : ''}`}
                {start !== null ? ` (${hhmm(start)}–${hhmm(start + l)})` : ''}
              </option>
            ))}
          </select>
        </Field>
        <Field label={r.unit}>
          <select value={unit} onChange={(e) => setUnitId(e.target.value)}>
            {choices.map((u) => (
              <option key={u.id} value={u.id}>
                {u.label}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label={r.notes}>
        <input value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
      {start !== null && overlap && <Alert>{r.rules.overlap}</Alert>}
      {start !== null && !overlap && violation && <Alert tone={manages ? 'info' : 'error'}>{r.rules[violation]}</Alert>}
      {book.isError && <Alert>{bookingError(book.error, t)}</Alert>}
      {done && <Alert tone="info">{done.status === 'requested' ? r.requested : r.booked}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={blocked || book.isPending || !unit}>
          {resource.requiresApproval && !manages ? r.request : resource.feeCents ? r.bookFee(money(resource.feeCents)) : r.book}
        </button>
      </div>
    </form>
  );
}

// --- Bookings

function BookingsList({ scope }: { scope: 'mine' | 'all' }) {
  const { t, locale, money } = useI18n();
  const r = t.reservations;
  const s = useSyndicate();
  const manages = s.can('reservations.manage');
  const bookings = useReservations<Booking[]>(['bookings', scope], `/bookings?scope=${scope}`);
  const resources = useReservations<Resource[]>(['resources'], '/resources');
  const cancel = useReservationMutation<string, Booking>((api, base, bookingId) => api(`${base}/bookings/${bookingId}/cancel`, { method: 'POST' }));
  const decide = useReservationMutation<{ id: string; outcome: 'confirmed' | 'rejected' }, Booking>((api, base, { id, outcome }) => api(`${base}/bookings/${id}/decide`, { method: 'POST', body: { outcome } }));
  const list = (bookings.data ?? []).filter((b) => b.status === 'requested' || b.status === 'confirmed');
  const sorted = scope === 'all' ? [...list].sort((a, b) => Number(b.status === 'requested') - Number(a.status === 'requested')) : list;

  if (bookings.isPending) return <Loading label={t.loading} />;
  if (bookings.isError) return <Alert>{errorMessage(bookings.error, t)}</Alert>;
  if (sorted.length === 0) return <Empty>{r.none}</Empty>;
  return (
    <>
      {(cancel.isError || decide.isError) && <Alert>{bookingError(cancel.error ?? decide.error, t)}</Alert>}
      <ul className="booking-list">
        {sorted.map((b) => {
          const resource = resources.data?.find((x) => x.id === b.resourceId);
          const deadline = resource ? new Date(Date.parse(b.start) - resource.cancelNoticeHours * 3_600_000) : null;
          const canCancel = manages || (deadline !== null && deadline.getTime() > Date.now());
          return (
            <li key={b.id} className={`booking booking-${b.status}`}>
              <div>
                <strong>{b.resourceName}</strong> <span className={`badge badge-booking-${b.status}`}>{r.statuses[b.status]}</span>
                <div className="small">
                  {dayOf(b.start, locale)}, {timeOf(b.start, locale)}–{timeOf(b.end, locale)} · {r.unit} {b.unitLabel}
                  {b.bookedByName && scope === 'all' ? ` · ${r.bookedBy(b.bookedByName)}` : ''}
                </div>
                <div className="small muted">
                  {[b.feeCents ? r.fee(money(b.feeCents)) : null, b.depositCents ? r.deposit(money(b.depositCents)) : null, b.notes || null].filter(Boolean).join(' · ')}
                </div>
                {!manages && deadline && <div className="small muted">{canCancel ? r.cancelUntil(`${dayOf(deadline.toISOString(), locale)} ${timeOf(deadline.toISOString(), locale)}`) : r.cancelLate}</div>}
              </div>
              <div className="booking-actions">
                {manages && b.status === 'requested' && (
                  <>
                    <button type="button" className="button button-small button-primary" disabled={decide.isPending} onClick={() => decide.mutate({ id: b.id, outcome: 'confirmed' })}>
                      {r.approve}
                    </button>
                    <button type="button" className="button button-small" disabled={decide.isPending} onClick={() => decide.mutate({ id: b.id, outcome: 'rejected' })}>
                      {r.reject}
                    </button>
                  </>
                )}
                {canCancel && (b.mine || manages) && (
                  <button type="button" className="link-button small" disabled={cancel.isPending} onClick={() => cancel.mutate(b.id)}>
                    {r.cancel}
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
}

// --- Facilities (board)

function FacilitiesTab() {
  const { t, money } = useI18n();
  const r = t.reservations;
  const resources = useReservations<Resource[]>(['resources'], '/resources');
  const [editing, setEditing] = useState<string | null>(null);
  return (
    <>
      <Disclosure label={r.addFacility}>{(close) => <ResourceForm onDone={close} />}</Disclosure>
      {resources.isPending && <Loading label={t.loading} />}
      {resources.data?.map((x) =>
        editing === x.id ? (
          <section key={x.id} className="panel">
            <ResourceForm resource={x} onDone={() => setEditing(null)} />
          </section>
        ) : (
          <section key={x.id} className="panel facility-admin">
            <header className="dash-card-header">
              <h2>
                {x.name} <span className="badge">{r.kinds[x.kind]}</span> {!x.active && <span className="badge badge-urgent">{r.inactive}</span>}
              </h2>
              <button type="button" className="link-button small" onClick={() => setEditing(x.id)}>
                {r.edit}
              </button>
            </header>
            <p className="small muted">
              {[r.hours(x.opensAt, x.closesAt), r.duration(x.minMinutes, x.maxMinutes), x.feeCents ? r.fee(money(x.feeCents)) : r.free, x.weeklyQuota ? r.quota(x.weeklyQuota) : null, x.requiresApproval ? r.approval : null]
                .filter(Boolean)
                .join(' · ')}
            </p>
            <Blackouts resource={x} />
          </section>
        ),
      )}
    </>
  );
}

function Blackouts({ resource }: { resource: Resource }) {
  const { t, locale } = useI18n();
  const r = t.reservations;
  const [values, setValues] = useState({ from: '', to: '', reason: '' });
  const add = useReservationMutation<BlackoutInput, void>((api, base, input) => api(`${base}/resources/${resource.id}/blackouts`, { method: 'POST', body: input }));
  const remove = useReservationMutation<string, void>((api, base, id) => api(`${base}/blackouts/${id}`, { method: 'DELETE' }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!values.from || !values.to) return;
    add.mutate({ start: localMidnight(values.from), end: localMidnight(addDays(values.to, 1)), reason: values.reason }, { onSuccess: () => setValues({ from: '', to: '', reason: '' }) });
  };
  return (
    <details>
      <summary>
        {r.blackouts} ({resource.blackouts.length})
      </summary>
      <ul className="compact-list">
        {resource.blackouts.map((b) => (
          <li key={b.id}>
            <span>
              {dayOf(b.start, locale)} → {dayOf(new Date(Date.parse(b.end) - 1).toISOString(), locale)}
            </span>
            <span className="muted">{b.reason}</span>
            <button type="button" className="link-button small" onClick={() => remove.mutate(b.id)}>
              {r.remove}
            </button>
          </li>
        ))}
      </ul>
      <form className="row" onSubmit={submit}>
        <Field label={r.from}>
          <input type="date" value={values.from} onChange={(e) => setValues((v) => ({ ...v, from: e.target.value }))} />
        </Field>
        <Field label={r.to}>
          <input type="date" value={values.to} min={values.from} onChange={(e) => setValues((v) => ({ ...v, to: e.target.value }))} />
        </Field>
        <Field label={r.reason}>
          <input value={values.reason} onChange={(e) => setValues((v) => ({ ...v, reason: e.target.value }))} />
        </Field>
        <div className="form-actions">
          <button type="submit" className="button button-small" disabled={add.isPending}>
            {r.addBlackout}
          </button>
        </div>
      </form>
      {add.isError && <Alert>{errorMessage(add.error, t)}</Alert>}
    </details>
  );
}

function ResourceForm({ resource, onDone }: { resource?: Resource; onDone: () => void }) {
  const { t } = useI18n();
  const r = t.reservations;
  const [values, setValues] = useState({
    name: resource?.name ?? '',
    kind: resource?.kind ?? 'room',
    description: resource?.description ?? '',
    location: resource?.location ?? '',
    opensAt: resource?.opensAt ?? '08:00',
    closesAt: resource?.closesAt ?? '22:00',
    slotMinutes: String(resource?.slotMinutes ?? 30),
    minMinutes: String(resource?.minMinutes ?? 60),
    maxMinutes: String(resource?.maxMinutes ?? 240),
    maxDaysAhead: String(resource?.maxDaysAhead ?? 60),
    minNoticeHours: String(resource?.minNoticeHours ?? 2),
    cancelNoticeHours: String(resource?.cancelNoticeHours ?? 24),
    weeklyQuota: resource?.weeklyQuota?.toString() ?? '',
    fee: resource ? (resource.feeCents / 100).toFixed(2) : '0',
    deposit: resource ? (resource.depositCents / 100).toFixed(2) : '0',
    rulesText: resource?.rulesText ?? '',
  });
  const [flags, setFlags] = useState({ requiresApproval: resource?.requiresApproval ?? false, active: resource?.active ?? true });
  const [errors, setErrors] = useState<FieldErrors>({});
  const save = useReservationMutation<ResourceInput, Resource>((api, base, input) => api(resource ? `${base}/resources/${resource.id}` : `${base}/resources`, { method: resource ? 'PUT' : 'POST', body: input }));
  const set = (key: keyof typeof values) => (e: { target: { value: string } }) => setValues((v) => ({ ...v, [key]: e.target.value }));
  const cents = (v: string) => Math.round((toNumber(v) ?? 0) * 100);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const { fee, deposit, weeklyQuota, ...rest } = values;
    const result = validate(ResourceInput, {
      ...rest,
      ...flags,
      slotMinutes: toNumber(values.slotMinutes),
      minMinutes: toNumber(values.minMinutes),
      maxMinutes: toNumber(values.maxMinutes),
      maxDaysAhead: toNumber(values.maxDaysAhead),
      minNoticeHours: toNumber(values.minNoticeHours),
      cancelNoticeHours: toNumber(values.cancelNoticeHours),
      weeklyQuota: weeklyQuota ? toNumber(weeklyQuota) : null,
      feeCents: cents(fee),
      depositCents: cents(deposit),
    });
    setErrors(result.errors ?? {});
    if (result.data) save.mutate(result.data, { onSuccess: onDone });
  };
  const num = (key: keyof typeof values, label: string) => (
    <Field label={label} error={errors[key]}>
      <input inputMode="numeric" value={values[key]} onChange={set(key)} />
    </Field>
  );
  return (
    <form onSubmit={submit} className="form" noValidate>
      <div className="row">
        <Field label={r.name} error={errors.name}>
          <input value={values.name} onChange={set('name')} autoFocus />
        </Field>
        <Field label={r.kind}>
          <select value={values.kind} onChange={set('kind')}>
            {ResourceKind.options.map((k) => (
              <option key={k} value={k}>
                {r.kinds[k]}
              </option>
            ))}
          </select>
        </Field>
        <Field label={r.location}>
          <input value={values.location} onChange={set('location')} />
        </Field>
      </div>
      <Field label={r.description}>
        <input value={values.description} onChange={set('description')} />
      </Field>
      <div className="row">
        <Field label={r.opensAt} error={errors.opensAt}>
          <input type="time" value={values.opensAt} onChange={set('opensAt')} />
        </Field>
        <Field label={r.closesAt} error={errors.closesAt}>
          <input type="time" value={values.closesAt} onChange={set('closesAt')} />
        </Field>
        <Field label={r.slotMinutes}>
          <select value={values.slotMinutes} onChange={set('slotMinutes')}>
            {['15', '30', '60'].map((v) => (
              <option key={v}>{v}</option>
            ))}
          </select>
        </Field>
        {num('minMinutes', r.minMinutes)}
        {num('maxMinutes', r.maxMinutes)}
      </div>
      <div className="row">
        {num('maxDaysAhead', r.maxDaysAhead)}
        {num('minNoticeHours', r.minNoticeHours)}
        {num('cancelNoticeHours', r.cancelNoticeHours)}
        {num('weeklyQuota', r.weeklyQuota)}
      </div>
      <div className="row">
        <Field label={r.feeLabel}>
          <input inputMode="decimal" value={values.fee} onChange={set('fee')} />
        </Field>
        <Field label={r.depositLabel}>
          <input inputMode="decimal" value={values.deposit} onChange={set('deposit')} />
        </Field>
      </div>
      <Field label={r.rulesText}>
        <textarea rows={3} value={values.rulesText} onChange={set('rulesText')} />
      </Field>
      <label className="inline-field">
        <input type="checkbox" checked={flags.requiresApproval} onChange={(e) => setFlags((f) => ({ ...f, requiresApproval: e.target.checked }))} />
        {r.requiresApproval}
      </label>
      <label className="inline-field">
        <input type="checkbox" checked={flags.active} onChange={(e) => setFlags((f) => ({ ...f, active: e.target.checked }))} />
        {r.active}
      </label>
      {save.isError && <Alert>{errorMessage(save.error, t)}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={save.isPending}>
          {t.common.save}
        </button>
        <button type="button" className="button" onClick={onDone}>
          {t.common.cancel}
        </button>
      </div>
    </form>
  );
}
